import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Save, Settings2, ShieldAlert, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
	type AgentQualityMode,
	type AgentWriteMode,
	useAgentRuntimeConfig,
	usePutAgentRuntimeConfig,
} from "@/lib/api/runtime";
import { type OAuthHealthResponse, useOAuthHealth } from "@/lib/api/skills";

export const Route = createFileRoute("/runtime-closure")({
	component: RuntimeClosurePage,
});

const DEFAULT_MERCHANT_ID = "mer_devcons";

type RuntimeRowTone = "success" | "warning" | "neutral" | "danger";

interface RuntimeRow {
	area: string;
	state: string;
	tone: RuntimeRowTone;
	evidence: string;
	next: string;
}

// 非 Shopify 行保持静态（本就是谨慎态，不构成假绿灯）；Shopify 行改由 live probe 驱动。
const STATIC_RUNTIME_ROWS: RuntimeRow[] = [
	{
		area: "Qdrant runtime",
		state: "未配置",
		tone: "warning",
		evidence:
			"M09 当前代表本地检索/沙盒，真实 RAG runtime 待 URL、key、collection 接入（见知识管理页索引状态）。",
		next: "配置 collection 与 ingestion",
	},
	{
		area: "写操作",
		state: "默认保护性试跑",
		tone: "neutral",
		evidence: "取消、退款、改地址等写能力已有 contract，但未开放真实执行。",
		next: "真实写入必须二次确认",
	},
];

// 由 oauth-health live probe 驱动 Shopify 依赖行（替代硬编码"真实只读已通"绿灯）。
function buildShopifyRows(
	health: OAuthHealthResponse | undefined,
	isLoading: boolean,
	errored: boolean,
): RuntimeRow[] {
	const apiStatus = health?.shopify_api_status;
	let shop: RuntimeRow;
	if (isLoading) {
		shop = {
			area: "Shopify 商品/店铺",
			state: "探测中…",
			tone: "neutral",
			evidence: "正在发起只读 Shopify shop GraphQL probe。",
			next: "稍候",
		};
	} else if (errored) {
		shop = {
			area: "Shopify 商品/店铺",
			state: "受限·探测失败",
			tone: "danger",
			evidence: "无法发起连通探测（接口报错或商户不存在），不能据此宣称已接入。",
			next: "检查商户 ID 与后端",
		};
	} else if (apiStatus === "ok") {
		shop = {
			area: "Shopify 商品/店铺",
			state: "真实只读已通",
			tone: "success",
			evidence: health?.message ?? "Shopify Admin GraphQL probe 成功。",
			next: "继续保留 smoke 记录",
		};
	} else if (apiStatus === "warning") {
		shop = {
			area: "Shopify 商品/店铺",
			state: "返回异常",
			tone: "warning",
			evidence: health?.message ?? "probe 返回结构异常。",
			next: "核对 scopes 与响应",
		};
	} else if (apiStatus === "error") {
		shop = {
			area: "Shopify 商品/店铺",
			state: "受限·连通失败",
			tone: "danger",
			evidence: health?.message ?? "Shopify 连通失败（access token / OAuth 未通）。",
			next: "检查 access token / OAuth 安装",
		};
	} else {
		shop = {
			area: "Shopify 商品/店铺",
			state: "未探测",
			tone: "neutral",
			evidence: "尚未发起 live probe。",
			next: "点「重新探测」",
		};
	}

	const connected = apiStatus === "ok";
	const orders: RuntimeRow = connected
		? {
				area: "Shopify 订单/客户",
				state: "PCD 阻塞",
				tone: "warning",
				evidence: "基础连通已通过；订单与客户事实需 Protected Customer Data 获批后才能宣称接入。",
				next: "审批 PCD 后准备测试订单样本",
			}
		: {
				area: "Shopify 订单/客户",
				state: "受限·连通未通过",
				tone: "danger",
				evidence:
					"基础 Shopify 连通尚未通过（token/OAuth），订单与客户无从读取——根因是接入未通，并非仅 PCD 审批。",
				next: "先打通基础连通，再谈 PCD",
			};

	return [shop, orders];
}

const ACCEPTANCE_ITEMS = ["创建测试订单", "查询订单", "RAG snippet 进入证据区", "真实写入二次确认"];

function RuntimeClosurePage() {
	const [merchantId, setMerchantId] = useState(DEFAULT_MERCHANT_ID);
	const activeMerchantId = merchantId.trim();
	const runtimeConfig = useAgentRuntimeConfig(activeMerchantId);
	const saveRuntimeConfig = usePutAgentRuntimeConfig(activeMerchantId);
	const oauthHealth = useOAuthHealth(activeMerchantId);
	const runtimeRows = useMemo(
		() => [
			...buildShopifyRows(oauthHealth.data, oauthHealth.isLoading, oauthHealth.isError),
			...STATIC_RUNTIME_ROWS,
		],
		[oauthHealth.data, oauthHealth.isLoading, oauthHealth.isError],
	);
	const [sdkLoopEnabled, setSdkLoopEnabled] = useState(true);
	const [maxSteps, setMaxSteps] = useState(6);
	const [qualityMode, setQualityMode] = useState<AgentQualityMode>("q_deep_side_effect");
	const [writeMode, setWriteMode] = useState<AgentWriteMode>("live_confirmed");
	const [runtimeNotes, setRuntimeNotes] = useState("");
	const [runtimeStatus, setRuntimeStatus] = useState<string | null>(null);

	// 记录已经把表单同步过的商户；用于区分「切换商户重载」与「保存后同商户重拉」。
	const loadedMerchantRef = useRef<string | null>(null);
	useEffect(() => {
		if (!runtimeConfig.data) return;
		setSdkLoopEnabled(runtimeConfig.data.sdk_loop_enabled);
		setMaxSteps(runtimeConfig.data.max_steps);
		setQualityMode(runtimeConfig.data.quality_mode);
		setWriteMode(runtimeConfig.data.write_mode);
		setRuntimeNotes(runtimeConfig.data.notes ?? "");
		// 仅当加载的是「不同商户」的配置时清空保存提示；保存成功后 invalidate→重拉是同一商户，
		// 此时保留「已保存 · …」不被误清回占位（之前把 setRuntimeStatus(null) 无条件放这里就是该 bug 根因）。
		if (loadedMerchantRef.current !== activeMerchantId) {
			loadedMerchantRef.current = activeMerchantId;
			setRuntimeStatus(null);
		}
	}, [runtimeConfig.data, activeMerchantId]);

	const effectiveSummary = useMemo(
		() => runtimeSummary({ sdkLoopEnabled, maxSteps, qualityMode, writeMode }),
		[sdkLoopEnabled, maxSteps, qualityMode, writeMode],
	);
	let runtimeStatusText = "配置变更保存后，会在链路测试台下一次运行时生效。";
	if (runtimeConfig.isLoading) {
		runtimeStatusText = "加载运行参数中...";
	} else if (saveRuntimeConfig.isPending) {
		runtimeStatusText = "保存中...";
	} else if (runtimeStatus) {
		runtimeStatusText = runtimeStatus;
	}

	function handleSaveRuntimeConfig() {
		if (!activeMerchantId) return;
		setRuntimeStatus(null);
		saveRuntimeConfig.mutate(
			{
				sdk_loop_enabled: sdkLoopEnabled,
				max_steps: maxSteps,
				quality_mode: qualityMode,
				write_mode: writeMode,
				updated_by: "admin-console",
				notes: runtimeNotes.trim() ? runtimeNotes.trim() : null,
			},
			{
				onSuccess: (data) => {
					setRuntimeStatus(`已保存 · ${runtimeSummary(data)}`);
				},
				onError: (error) => {
					setRuntimeStatus(error instanceof Error ? error.message : "保存失败");
				},
			},
		);
	}

	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5">
			<PageHeader
				moduleId="P5"
				title="接入状态"
				description="说明 Shopify、Qdrant、写操作、权限和保护性试跑边界，判断测试结果能否代表真实系统。"
				help={runtimeHelp}
			/>

			<Card id="agent-runtime-config" data-testid="p5-runtime-config">
				<CardHeader>
					<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
						<div>
							<CardTitle className="flex items-center gap-2 text-lg">
								<Settings2 className="h-4 w-4 text-brand-primary" aria-hidden="true" />
								主链路运行配置
							</CardTitle>
							<CardDescription>
								维护下一次链路运行会用到的只读补查、工具步数、质量检查和写动作策略；右侧展示后端强制安全边界。
							</CardDescription>
						</div>
						<div
							className="rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm text-text-primary lg:min-w-[420px]"
							data-testid="p5-runtime-summary"
						>
							<div className="text-xs text-text-muted">当前生效摘要</div>
							<div className="mt-1 font-medium">
								{runtimeConfig.isLoading ? "加载运行参数中..." : effectiveSummary}
							</div>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					<div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
						<div className="space-y-3">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								<label className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">商户 ID</span>
									<input
										value={merchantId}
										onChange={(event) => setMerchantId(event.target.value)}
										className="h-9 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
										data-testid="p5-runtime-merchant"
									/>
								</label>
								<span className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">只读工具补查</span>
									<span className="flex h-9 items-center justify-between gap-3 rounded-md border border-surface-border bg-surface-background px-3">
										<span className="font-medium text-text-primary">
											{sdkLoopEnabled ? "已启用" : "关闭"}
										</span>
										<Switch
											checked={sdkLoopEnabled}
											onCheckedChange={setSdkLoopEnabled}
											data-testid="p5-sdk-loop"
										/>
									</span>
								</span>
								<label className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">工具步数上限</span>
									<input
										type="number"
										min={1}
										max={12}
										value={maxSteps}
										onChange={(event) =>
											setMaxSteps(Math.max(1, Math.min(12, Number(event.target.value) || 1)))
										}
										className="h-9 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
										data-testid="p5-max-steps"
									/>
								</label>
								<div className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">质量检查</span>
									<Select
										className="w-full"
										aria-label="质量检查"
										value={qualityMode}
										onChange={(event) => setQualityMode(event.target.value as AgentQualityMode)}
										data-testid="p5-quality-mode"
									>
										<option value="q_deep_side_effect">严格检查：质量 + 副作用</option>
										<option value="q_fast_grounding_side_effect">快速检查：事实 + 副作用</option>
									</Select>
								</div>
							</div>

							<div className="grid gap-3 lg:grid-cols-[260px_minmax(0,1fr)_160px]">
								<div className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">写动作策略</span>
									<Select
										className="w-full"
										aria-label="写动作策略"
										value={writeMode}
										onChange={(event) => setWriteMode(event.target.value as AgentWriteMode)}
										data-testid="p5-write-mode"
									>
										<option value="disabled">不允许写动作</option>
										<option value="dry_run">只生成试跑计划</option>
										<option value="live_confirmed">确认后允许执行</option>
									</Select>
								</div>
								<label className="grid gap-1 text-sm">
									<span className="text-xs text-text-muted">变更备注</span>
									<input
										value={runtimeNotes}
										onChange={(event) => setRuntimeNotes(event.target.value)}
										className="h-9 rounded-md border border-surface-border bg-surface-background px-3 text-sm"
										placeholder="记录本次切换原因"
										data-testid="p5-runtime-notes"
									/>
								</label>
								<div className="flex items-end">
									<Button
										type="button"
										onClick={handleSaveRuntimeConfig}
										disabled={!activeMerchantId || saveRuntimeConfig.isPending}
										data-testid="p5-save-runtime"
									>
										<Save className="mr-2 h-4 w-4" aria-hidden="true" />
										保存配置
									</Button>
								</div>
							</div>

							<div
								className="rounded-md bg-surface-subtle px-3 py-2 text-sm text-text-secondary"
								data-testid="p5-runtime-status"
							>
								{runtimeStatusText}
							</div>
						</div>

						<div className="space-y-3 rounded-md border border-surface-border bg-surface-background p-3">
							<div className="flex items-center justify-between gap-3">
								<div>
									<div className="text-sm font-semibold text-text-primary">写入安全边界</div>
									<div className="text-xs text-text-muted">后端返回的只读结果，前端不能绕过。</div>
								</div>
								<RuntimePill
									tone={
										runtimeConfig.data?.live_write_mutation_direct_from_agent
											? "blocked"
											: "success"
									}
								>
									{runtimeConfig.data?.live_write_mutation_direct_from_agent
										? "主 Agent 可直写"
										: "主 Agent 不直写"}
								</RuntimePill>
							</div>
							<div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
								<GuardrailTile
									label="人工确认"
									value={runtimeConfig.data?.live_write_requires_confirmation ? "必须" : "未要求"}
									active={Boolean(runtimeConfig.data?.live_write_requires_confirmation)}
								/>
								<GuardrailTile
									label="质量复核"
									value={runtimeConfig.data?.live_write_requires_q_deep ? "必须" : "未要求"}
									active={Boolean(runtimeConfig.data?.live_write_requires_q_deep)}
								/>
								<GuardrailTile
									label="直接写入"
									value={
										runtimeConfig.data?.live_write_mutation_direct_from_agent ? "允许" : "禁止"
									}
									active={!runtimeConfig.data?.live_write_mutation_direct_from_agent}
								/>
							</div>
							<div className="grid gap-2 rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
								<AuditLine label="更新人" value={runtimeConfig.data?.updated_by ?? "暂无记录"} />
								<AuditLine
									label="更新时间"
									value={formatRuntimeDate(runtimeConfig.data?.updated_at)}
								/>
								<AuditLine
									label="备注"
									value={runtimeNotes || runtimeConfig.data?.notes || "未填写"}
								/>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			<section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
				<Card>
					<CardHeader>
						<div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
							<div>
								<CardTitle className="text-lg">真实依赖状态</CardTitle>
								<CardDescription>
									Shopify 行来自 live probe（
									{oauthHealthCheckedLabel(oauthHealth.data, oauthHealth.isError)}
									），其余按阻塞/未配置/保护执行区分，不混用验收结论。
								</CardDescription>
							</div>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => oauthHealth.refetch()}
								disabled={!activeMerchantId || oauthHealth.isFetching}
								data-testid="p5-shopify-reprobe"
							>
								{oauthHealth.isFetching ? "探测中…" : "重新探测"}
							</Button>
						</div>
					</CardHeader>
					<CardContent className="space-y-3">
						{runtimeRows.map((row) => (
							<div
								key={row.area}
								className="grid gap-3 rounded-md border border-surface-border bg-surface-background p-3 md:grid-cols-[180px_130px_minmax(0,1fr)_180px]"
							>
								<div className="font-medium text-text-primary">{row.area}</div>
								<StatusPill tone={row.tone}>{row.state}</StatusPill>
								<div className="text-sm leading-6 text-text-secondary">{row.evidence}</div>
								<div className="text-sm text-text-muted">{row.next}</div>
							</div>
						))}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">验收清单</CardTitle>
						<CardDescription>外部依赖到位后，按这四项补真实端到端证据。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3">
						{ACCEPTANCE_ITEMS.map((item) => (
							<div
								key={item}
								className="flex items-center gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm text-text-primary"
							>
								<CheckCircle2 className="h-4 w-4 text-brand-primary" aria-hidden="true" />
								{item}
							</div>
						))}
					</CardContent>
				</Card>
			</section>

			<section className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
				<Card>
					<CardHeader>
						<CardTitle className="text-lg">风险边界</CardTitle>
						<CardDescription>真实写入和客户数据必须先保护，再验证。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3 text-sm leading-6 text-text-secondary">
						<div className="flex gap-3 rounded-md bg-surface-subtle p-3">
							<ShieldAlert
								className="mt-0.5 h-4 w-4 flex-none text-brand-warning"
								aria-hidden="true"
							/>
							<p>没有 PCD 时，订单读取失败是外部权限边界，不应在回复中生成假订单事实。</p>
						</div>
						<div className="flex gap-3 rounded-md bg-surface-subtle p-3">
							<ShieldAlert
								className="mt-0.5 h-4 w-4 flex-none text-brand-warning"
								aria-hidden="true"
							/>
							<p>没有二次确认和审计记录时，任何写操作都只能停留在保护性试跑。</p>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">关联验证入口</CardTitle>
						<CardDescription>
							接入状态只说明运行边界，具体效果仍要回到工具、知识和链路证据。
						</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-2 md:grid-cols-3">
						<QuickLink to="/skill-tool" label="工具与动作" />
						<QuickLink to="/knowledge" label="知识与证据" />
						<QuickLink to="/pipeline-testbench" label="链路测试台" />
					</CardContent>
				</Card>
			</section>
		</div>
	);
}

function StatusPill({
	children,
	tone,
}: {
	children: string;
	tone: RuntimeRowTone;
}) {
	const toneClass =
		tone === "success"
			? "border-brand-success/30 bg-brand-success/10 text-brand-success"
			: tone === "warning"
				? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
				: tone === "danger"
					? "border-brand-danger/30 bg-brand-danger/10 text-brand-danger"
					: "border-surface-border bg-surface-subtle text-text-secondary";
	return (
		<span
			className={`inline-flex w-fit rounded-md border px-2 py-1 text-xs font-medium ${toneClass}`}
		>
			{children}
		</span>
	);
}

function oauthHealthCheckedLabel(
	health: OAuthHealthResponse | undefined,
	errored: boolean,
): string {
	if (errored) return "探测失败";
	if (!health) return "探测中…";
	if (!health.checked_live) return "未实测";
	if (health.shopify_api_status === "ok") return "实测已通";
	if (health.shopify_api_status === "error") return "实测未通";
	if (health.shopify_api_status === "warning") return "实测异常";
	return "已实测";
}

function RuntimePill({
	children,
	tone,
}: {
	children: string;
	tone: "success" | "warning" | "blocked" | "neutral";
}) {
	const toneClass =
		tone === "success"
			? "border-brand-success/30 bg-brand-success/10 text-brand-success"
			: tone === "warning"
				? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
				: tone === "blocked"
					? "border-brand-danger/30 bg-brand-danger/10 text-brand-danger"
					: "border-surface-border bg-surface-subtle text-text-secondary";
	return (
		<span
			className={`inline-flex w-fit rounded-md border px-2 py-1 text-xs font-medium ${toneClass}`}
		>
			{children}
		</span>
	);
}

function GuardrailTile({
	label,
	value,
	active,
}: {
	label: string;
	value: string;
	active: boolean;
}) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-card p-2">
			<div className="flex items-center gap-2 text-xs font-medium text-text-primary">
				<ShieldCheck
					className={`h-3.5 w-3.5 ${active ? "text-brand-primary" : "text-text-muted"}`}
					aria-hidden="true"
				/>
				{label}
			</div>
			<div className="mt-1 font-mono text-[11px] text-text-muted">{value}</div>
		</div>
	);
}

function AuditLine({ label, value }: { label: string; value: string }) {
	return (
		<div className="grid gap-2 sm:grid-cols-[72px_minmax(0,1fr)]">
			<span className="text-text-muted">{label}</span>
			<span className="break-words font-medium text-text-primary">{value}</span>
		</div>
	);
}

function QuickLink({
	to,
	label,
}: {
	to: "/skill-tool" | "/knowledge" | "/pipeline-testbench";
	label: string;
}) {
	return (
		<Link
			to={to}
			className="inline-flex items-center justify-between rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-subtle"
		>
			{label}
			<ArrowRight className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
		</Link>
	);
}

function runtimeSummary(config: {
	sdkLoopEnabled?: boolean;
	sdk_loop_enabled?: boolean;
	maxSteps?: number;
	max_steps?: number;
	qualityMode?: AgentQualityMode;
	quality_mode?: AgentQualityMode;
	writeMode?: AgentWriteMode;
	write_mode?: AgentWriteMode;
}): string {
	const sdkLoopEnabled = config.sdkLoopEnabled ?? config.sdk_loop_enabled ?? false;
	const maxSteps = config.maxSteps ?? config.max_steps ?? 0;
	const qualityMode = config.qualityMode ?? config.quality_mode ?? "q_deep_side_effect";
	const writeMode = config.writeMode ?? config.write_mode ?? "disabled";
	return `${sdkLoopEnabled ? "只读补查已启用" : "只读补查关闭"} · 最多 ${maxSteps} 步 · ${runtimeQualityModeLabel(qualityMode)} · ${runtimeWriteModeLabel(writeMode)}`;
}

function runtimeQualityModeLabel(mode: AgentQualityMode): string {
	if (mode === "q_fast_grounding_side_effect") return "快速检查：事实 + 副作用";
	return "严格检查：质量 + 副作用";
}

function runtimeWriteModeLabel(mode: AgentWriteMode): string {
	if (mode === "live_confirmed") return "确认后允许执行";
	if (mode === "dry_run") return "只生成试跑计划";
	return "不允许写动作";
}

function formatRuntimeDate(value?: string | null): string {
	if (!value) return "暂无记录";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("zh-CN", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	}).format(date);
}

const runtimeHelp: PageHelp = {
	title: "接入状态说明",
	description: "这里用于判断哪些能力已经真实接入，哪些仍是受限、未配置或保护执行。",
	sections: [
		{
			title: "怎么判断",
			items: [
				"主链路运行配置来自 runtime API，保存后影响下一次链路运行。",
				"真实只读已通表示可以读取真实外部事实，但不会写入。",
				"PCD 阻塞表示外部权限未获批，不是系统功能已经失败。",
				"默认保护性试跑表示写能力只验证规则，不改真实订单。",
			],
		},
		{
			title: "上线前必须补什么",
			items: [
				"Shopify PCD 审批和测试订单样本。",
				"Qdrant runtime URL、API key、collection 和 ingestion 证据。",
				"真实写入二次确认、审计记录和回滚说明。",
			],
		},
	],
};
